import { getPreferredLanguages } from "../../../app/api/getPreferredLanguages";
import { BaseAction } from "../../../core/actions/BaseAction";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IPreferredLanguage } from "../../../Orchestrator/Types/Index";

// sessionStorage key — the cached list lives for the whole browser-tab session
// (survives page reloads and client-side navigation, cleared when the tab closes).
const PREFERRED_LANGUAGES_CACHE_KEY = "preferredLanguagesCache";

const readCachedLanguages = (): IPreferredLanguage[] | null => {
    if (typeof window === "undefined") return null;
    try {
        const raw = sessionStorage.getItem(PREFERRED_LANGUAGES_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
        return null;
    }
};

const writeCachedLanguages = (languages: IPreferredLanguage[]): void => {
    if (typeof window === "undefined") return;
    try {
        sessionStorage.setItem(PREFERRED_LANGUAGES_CACHE_KEY, JSON.stringify(languages));
    } catch (error: any) {
        console.error("Error caching preferred languages:", error);
    }
};

/**
 * Fetches the customer preferred-languages list once and caches it for the
 * whole session. The list is mirrored into `languagesResponseAtom` (so any
 * component can read it) and persisted to `sessionStorage`, so it survives page
 * reloads and client-side navigation. Subsequent executions are no-ops served
 * from cache — the API is hit at most once per browser-tab session.
 *
 * Note: we cannot cache via `this.result` — BaseAction resets `_result` to null
 * before every run() — hence the sessionStorage + in-memory cache below.
 *
 * This action is intentionally free of UI side effects (it does NOT open the
 * language dropdown). Opening the dropdown is handled separately
 * (OpenLanguageDropdownAction) so the list can be pre-fetched at app startup
 * without popping the menu open.
 */
class GetPreferredLanguagesAction extends BaseAction<IPreferredLanguage[], void> {
    // Coalesce concurrent calls (e.g. the startup prefetch and an immediate
    // dropdown click) into a single network request.
    protected override deduplicate = true;

    // In-memory fast path; backed by sessionStorage so it survives reloads.
    private cachedLanguages: IPreferredLanguage[] | null = null;

    override async run(): Promise<IPreferredLanguage[]> {
        // 1) In-memory cache (same singleton, no page reload).
        if (this.cachedLanguages && this.cachedLanguages.length > 0) {
            return this.cachedLanguages;
        }

        // 2) sessionStorage cache (survives reload / navigation within the tab).
        const persisted = readCachedLanguages();
        if (persisted) {
            this.cachedLanguages = persisted;
            updateRecoilState("languagesResponseAtom", persisted);
            return persisted;
        }

        // 3) Cold — fetch once and cache.
        try {
            const response: any = await getPreferredLanguages();
            if (response.status === "ok" && response.answer) {
                const languageList: IPreferredLanguage[] = response.answer;
                if (languageList.length > 0) {
                    this.cachedLanguages = languageList;
                    writeCachedLanguages(languageList);
                    updateRecoilState("languagesResponseAtom", languageList);
                    return languageList;
                }
                console.error("No language available.");
            } else {
                console.error("Failed to fetch language.");
            }
        } catch (error: any) {
            console.error("Error fetching language:", error);
        }

        return this.cachedLanguages ?? [];
    }
}

export const preferredLanguagesAction = new GetPreferredLanguagesAction()
